import { Controller } from "@hotwired/stimulus";

/**
 * Latency Countdown Controller
 *
 * Animates a number counting down from 1000 to 5 very quickly
 * when the element enters the viewport, creating a "wow this is fast" effect.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["number"];

  declare readonly numberTarget: HTMLElement;

  private observer: IntersectionObserver | null = null;
  private hasAnimated: boolean = false;

  connect(): void {
    this.setupIntersectionObserver();
  }

  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.animateCountdown();
          }
        });
      },
      { threshold: 0.3 }
    );

    this.observer.observe(this.element);
  }

  private animateCountdown(): void {
    const startValue = 1000;
    const endValue = 5;
    const duration = 1500; // Total animation time in ms

    // Easing function - starts fast, slows down at the end
    const easeOut = (t: number): number => {
      return 1 - Math.pow(1 - t, 3);
    };

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing
      const easedProgress = easeOut(progress);

      // Calculate current value (counting down)
      const currentValue = Math.round(
        startValue - (startValue - endValue) * easedProgress
      );

      this.numberTarget.textContent = currentValue.toString();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at 5
        this.numberTarget.textContent = endValue.toString();
      }
    };

    requestAnimationFrame(animate);
  }
}
